import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement } from 'react';
import ReactMarkdown from 'react-markdown';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import ColumnSplittedLayout from '../../components/common/layout/ColumnSplittedLayout';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import { ContentSidebarBookmarks } from '../../components/common/ui';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  ICustomerReviewsMeta,
  ILayoutPageProps,
  ITermsPage,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { getRoutes } from '../../shared/useBuild';
import {
  buildApiKey,
  getCommonTranslations,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
} from '../../shared/useCommon';
import {
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import {
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getTranslation,
} from '../../shared/useUtils';
import { NextPageWithLayout } from '../_app';

export interface ITermsPageProps extends ILayoutPageProps {
  content: string;
  anchorHtmlTagName: string;
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
}

const TermsPolicyPage: NextPageWithLayout<ITermsPageProps> = (
  props: ITermsPageProps
) => {
  const router = useRouter();

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section">
        <div id="terms-main-content" className="page-content-block">
          <ReactMarkdown>{props.content}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

TermsPolicyPage.getLayout = function getLayout(
  page: ReactElement<ITermsPageProps>
) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      noindex={page.props.meta.noindex}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      <ColumnSplittedLayout
        sidebar={
          <ContentSidebarBookmarks
            bookmarkElementTagName={page.props.anchorHtmlTagName}
            contentElementId={'terms-main-content'}
          />
        }>
        {page}
      </ColumnSplittedLayout>
    </Layout>
  );
};

export async function getStaticProps(
  context: any
): Promise<{ props: ITermsPageProps }> {
  const appRoutes = await getRoutes();

  const pageUrlParams = new URLSearchParams({
    'populate[0]': 'meta',
    'populate[1]': 'content',
  });

  const page = (
    await fetchStrapi<any>(`/terms-page`, pageUrlParams, buildApiKey)
  ).data.attributes as ITermsPage;

  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = await populateFooterProps(context.locale);
  const commonTranslations = await getCommonTranslations();

  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );
  const pageSchema: PageJSONLDSchema = {
    nodes: [...websiteSchema, ...organizationSchema],
  };

  const meta = getTranslation(page.meta, context.locale);
  const alternativeLinks = getAlternateLinks('/terms', appRoutes);
  const canonicalLink = getCanonicalLink(context.locale || 'en', meta.slug);

  const props: ITermsPageProps = {
    // Layout
    pageSchema,
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta,
    alternativeLinks,
    canonicalLink,
    content: getTranslation(page.content, context.locale).text as string,
    anchorHtmlTagName: page.anchorHtmlTagName || 'h2',
  };

  return {
    props,
  };
}

export default TermsPolicyPage;
